// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { TimePeriod } from "types";
import { Aggregation } from "graphql/api";
import { IAction } from "actions";
import { IInput, IOutput } from "shared/types/parameter";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";
import { getIoType, getSiteSelection, getTrendIoGraphViewMode } from "guiutils";
import TrendWindowModel from "model/trendwindow";

import IoGraphAnalog from "view/comp/IoGraphAnalog";
import IoGraphDigital from "view/comp/IoGraphDigital";
import IoGraphDigitalCount from "view/comp/IoGraphDigitalCount";
import TrendHeading from "view/comp/TrendHeading";
import IoDatatable from "view/comp/IoDatatable";
import IoSiteBar from "view/comp/IoSiteBar";

interface IIoTrendExtProps {
  dashboardIndex?: number;
}
interface IIoTrendProps {
  inputs: IInput[] | undefined;
  outputs: IOutput[] | undefined;
  isFullTrendAvailable: boolean;
  timePeriod: TimePeriod;
  graphViewMode: GraphViewMode;
  aggregation: Aggregation;
  selectedIoType: "analog" | "digital";
}

const mapStateToProps = (state: IState, ownProps: IIoTrendExtProps) => {
  const selection = getSiteSelection(state);
  const selectedIoType = getIoType(state);
  const aggregation = TrendWindowModel.getIoAggregation(state);
  const graphViewMode =
    getTrendIoGraphViewMode(
      selection,
      selectedIoType,
      Object.keys(state.trending.ioTrendFull).length > 0,
      false,
    ) ?? GraphViewMode.BAR;

  const props: IIoTrendProps = {
    inputs: state.para.parameter?.input,
    outputs: state.para.parameter?.output,
    timePeriod: state.gui.ioPeriod.timePeriod,
    isFullTrendAvailable: Object.keys(state.trending.ioTrendFull).length > 0,
    graphViewMode,
    aggregation,
    selectedIoType,
  };
  return props;
};

interface IIoTrendDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoTrendDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  graphTitle: {
    id: "TrendGraphTitle",
    defaultMessage: "Trend",
  },
  timeGraphTitle: {
    id: "TrendTimeGraphTitle",
    defaultMessage: "Switching operations",
  },
});

class IoTrendComponent extends React.Component<IIoTrendProps & IIoTrendDispatch> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    return (
      <>
        <IoSiteBar showPeriodSelect />
        <div id="iotrend" className="flex-self flex-content">
          {this.renderTrendHeading()}
          {this.props.graphViewMode === GraphViewMode.TABLE ?
            <IoDatatable aggregation={this.props.aggregation} />
          : this.props.selectedIoType === "analog" ?
            <IoGraphAnalog />
          : this.props.graphViewMode === GraphViewMode.DIGITAL ?
            <IoGraphDigital />
            // bar and line views of aggregated digital data
          : <IoGraphDigitalCount />}
        </div>
      </>
    );
  }

  private renderTrendHeading() {
    const showGraphModes: GraphViewMode[] = [];
    if (this.props.selectedIoType === "digital" && !this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.BAR);
    }
    if (this.props.selectedIoType === "analog") {
      showGraphModes.push(GraphViewMode.LINE);
    }
    if (this.props.selectedIoType === "digital" && this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.DIGITAL);
    }
    if (this.props.selectedIoType === "analog" || this.props.isFullTrendAvailable) {
      showGraphModes.push(GraphViewMode.TABLE);
    }

    return (
      <TrendHeading
        identifier="ioPeriod"
        includeDate={true}
        showGraphModes={showGraphModes}
        title={this.context.formatMessage(
          this.props.selectedIoType === "digital" ? messages.timeGraphTitle : messages.graphTitle,
        )}
      />
    );
  }
}

const IoTrend = connect(mapStateToProps, mapDispatchToProps)(IoTrendComponent);

export default IoTrend;
